<script setup lang="ts">
import { computed } from 'vue';
import InfoContainer from '@/components/ui/InfoContainer.vue';
import type { Alert } from '@/@types/alert';
import { alertUrl, formatAlertCause, formatAlertEffect, formatAlertPublishedAgo } from '@/libs/alerts-helper';

const props = defineProps<{
  alert: Alert;
  /** Extra context shown before the cause and the timestamp, e.g. the stop name of a stop-only alert. */
  context?: string;
}>();

const open = defineModel<boolean>('open', { default: false });

const effect = computed<string | undefined>(() => formatAlertEffect(props.alert));

const url = computed<string | undefined>(() => alertUrl(props.alert));

const subtitle = computed<string | undefined>(() => {
  const parts = [props.context, formatAlertCause(props.alert), formatAlertPublishedAgo(props.alert)].filter(
    Boolean,
  );
  return parts.length > 0 ? parts.join(' · ') : undefined;
});
</script>

<template>
  <InfoContainer
    :id="alert._id"
    v-model:open="open"
    :expandable="!!alert.description_text"
    :subtitle="subtitle"
    :tag-label="effect"
    :title="alert.header_text"
  >
    <template v-if="url" #meta>
      <a
        :aria-label="$t('alerts.learnMoreAbout', { title: alert.header_text })"
        class="alert-card__link"
        :href="url"
        rel="noopener noreferrer"
        target="_blank"
      >
        {{ $t('alerts.learnMore') }}
      </a>
    </template>
    <div v-if="alert.description_text" v-safe-html="alert.description_text" class="alert-card__description" />
  </InfoContainer>
</template>

<style lang="scss">
.alert-card__link {
  color: $color-link;
  font: $label-medium;
  letter-spacing: $label-medium-spacing;
  overflow-wrap: anywhere;
}

.alert-card__description {
  overflow-wrap: anywhere;

  img {
    max-width: 100%;
  }
}
</style>
